import React, { useCallback, useEffect, useState } from 'react';
import { Lightbulb, X, Rocket } from 'lucide-react';
import { SuggestedTrend } from '@crewcast/shared';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { linkify } from '../utils/linkify';
import MomentCreatorModal from './MomentCreatorModal';

export default function SuggestedTrendsWidget() {
  const { token } = useAuth();
  const [trends, setTrends] = useState<SuggestedTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTrend, setActiveTrend] = useState<SuggestedTrend | null>(null);

  const fetchTrends = useCallback(async () => {
    try {
      if (!token) return;

      const res = await fetch(
        `${authConfig.apiUrl}/api/admin/suggested-trends`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) return;

      const data = await res.json();
      setTrends(data.trends || []);
    } catch {
      // Non-fatal — widget just stays empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  async function handleDismiss(trendId: string) {
    // Optimistically remove from UI
    setTrends((prev) => prev.filter((t) => t.trendId !== trendId));

    try {
      if (!token) return;

      await fetch(
        `${authConfig.apiUrl}/api/admin/suggested-trends/${trendId}/dismiss`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch {
      // Non-fatal — item is already removed from UI
    }
  }

  function handleLaunched(trendId: string) {
    setActiveTrend(null);
    setTrends((prev) => prev.filter((t) => t.trendId !== trendId));
  }

  return (
    <>
      <div className="suggested-trends-widget dashboard-widget">
        <div className="widget-header">
          <h3 className="widget-title">
            <span className="widget-title-icon">
              <Lightbulb size={20} color="#00bf62" />
            </span>
            Suggested for You
          </h3>
          {!loading && trends.length > 0 && (
            <span className="suggested-trends-count">{trends.length}</span>
          )}
        </div>

        {loading && <div className="suggested-trends-progress-bar" />}

        {!loading && trends.length === 0 && (
          <p className="suggested-trends-empty">No suggestions right now — check back soon.</p>
        )}

        {!loading && trends.length > 0 && (
        <ul className="suggested-trends-list">
          {trends.map((trend) => (
            <li key={trend.trendId} className="suggested-trend-card">
              <div className="suggested-trend-body">
                <div className="suggested-trend-header">
                  <span className="suggested-trend-title">{trend.title}</span>
                  <button
                    type="button"
                    className="suggested-trend-dismiss"
                    onClick={() => handleDismiss(trend.trendId)}
                    aria-label="Dismiss suggestion"
                    title="Not interested"
                  >
                    <X size={14} />
                  </button>
                </div>
                <p className="suggested-trend-description">{linkify(trend.description)}</p>
                <div className="suggested-trend-footer">
                  {trend.expiresAt && (
                    <span className="suggested-trend-badge">
                      <span className="suggested-trend-badge-label">Trending Until</span>
                      {new Date(trend.expiresAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  <button
                    type="button"
                    className="suggested-trend-launch"
                    onClick={() => setActiveTrend(trend)}
                  >
                    <Rocket size={14} />
                    Launch
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        )}
      </div>

      {activeTrend && (
        <MomentCreatorModal
          trend={activeTrend}
          onClose={() => setActiveTrend(null)}
          onLaunched={handleLaunched}
        />
      )}
    </>
  );
}
